import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Search, ArrowUpRight, Activity } from "lucide-react";

export default function Companies() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/companies${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setItems(data.companies || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div className="space-y-8" data-testid="companies-page">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-ink-line pb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand text-white grid place-items-center rounded-sm">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Admin · Company View</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Companies</h1>
            <p className="text-sm text-ink-muted mt-1.5">All users with bid activity. Open a company to see their full tender history and framework results.</p>
          </div>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="companies-search"
          placeholder="Search by company name or email…"
          className="w-full pl-9 pr-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
        />
      </div>

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3 font-medium">Company</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium text-right">Tenders</th>
              <th className="px-5 py-3 font-medium">Frameworks completed</th>
              <th className="px-5 py-3 font-medium">Last activity</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="px-5 py-10 text-center text-ink-muted uppercase tracking-wider text-xs">Loading…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={6} className="px-5 py-10 text-center text-ink-muted">No companies match.</td></tr>)}
            {items.map((c) => {
              const fc = c.framework_completion || {};
              return (
                <tr key={c.id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                  <td className="px-5 py-3">
                    <Link to={`/admin/companies/${c.id}`} className="font-medium text-ink hover:text-brand flex items-center gap-1.5" data-testid={`company-link-${c.id}`}>
                      <Activity className={`w-3 h-3 ${c.framework_total >= 3 ? "text-good" : c.framework_total > 0 ? "text-warn" : "text-ink-muted"}`} />
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-ink-muted text-xs">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-right font-num">{c.tender_count}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1.5 flex-wrap">
                      <span className={`chip text-[10px] ${fc.trca ? "bg-good-soft text-good border-good/40" : "bg-secondary text-ink-muted border-ink-line"}`}>TRCA {fc.trca ? "✓" : "—"}</span>
                      <span className={`chip text-[10px] ${fc.pcab ? "bg-good-soft text-good border-good/40" : "bg-secondary text-ink-muted border-ink-line"}`}>PCAB {fc.pcab ? "✓" : "—"}</span>
                      <span className={`chip text-[10px] ${fc.bnbd_count ? "bg-brand-soft text-brand border-brand/40" : "bg-secondary text-ink-muted border-ink-line"}`}>BNBD {fc.bnbd_count || 0}</span>
                      <span className={`chip text-[10px] ${fc.pcei_count ? "bg-brand-soft text-brand border-brand/40" : "bg-secondary text-ink-muted border-ink-line"}`}>PCEI {fc.pcei_count || 0}</span>
                      <span className={`chip text-[10px] ${fc.tapi_count ? "bg-brand-soft text-brand border-brand/40" : "bg-secondary text-ink-muted border-ink-line"}`}>TAPI {fc.tapi_count || 0}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-ink-muted font-num text-xs">{c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/admin/companies/${c.id}`} className="inline-flex items-center gap-1 text-brand hover:text-brand-hover text-[13px] font-medium">
                      Open <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
